// 4 Pillars of Motivation -> temperature (D22 / docs/09 §2).
//
// Temperature = follow-up FREQUENCY, not deal margin. So the rule is:
//   base       = the Timeline rating (Hot <30d / Warm 1–6mo / Cold 6+mo)
//   escalate   = if Reason is Hot, bump one step (Cold->Warm, Warm->Hot, Hot stays)
//   override   = a hard deadline within 30 days (foreclosure auction / tax sale) -> Hot
//   operator   = manual override always wins (handled in the UI)
//
// Price and Condition are captured as DEAL-QUALITY (they drive the Max Allowable
// Offer downstream) and shown on the lead — but they do NOT set temperature.
// This replaces the earlier Hot=2/Warm=1/Cold=0 average, which wrongly folded
// deal-quality into cadence.

export type Pillar = "reason" | "timeline" | "condition" | "price";
export type Temp = "hot" | "warm" | "cold";

// Which pillars drive cadence vs. deal-quality (mirrors config.qualification_rule).
export const CADENCE_PILLARS: Pillar[] = ["timeline", "reason"];
export const DEAL_QUALITY_PILLARS: Pillar[] = ["price", "condition"];

export const PILLARS: { key: Pillar; label: string; drives: "cadence" | "deal"; hint: Record<Temp, string> }[] = [
  { key: "timeline",  label: "Timeline",        drives: "cadence", hint: { hot: "< 30 days", warm: "1–6 months", cold: "6+ months" } },
  { key: "reason",    label: "Reason to sell",  drives: "cadence", hint: { hot: "clear distress", warm: "soft/vague", cold: "no real reason" } },
  { key: "condition", label: "Condition",       drives: "deal",    hint: { hot: "major work", warm: "some updates", cold: "good shape" } },
  { key: "price",     label: "Price vs. value", drives: "deal",    hint: { hot: "≤ 80%", warm: "80–100%", cold: "above value" } },
];

const escalate = (t: Temp): Temp => (t === "cold" ? "warm" : t === "warm" ? "hot" : "hot");

export type QualifyInput = {
  scores: Partial<Record<Pillar, Temp>>;
  hardDeadline?: boolean; // foreclosure auction / tax sale within 30 days
};

// Returns the computed temperature, or null if Timeline isn't set yet.
export function computeTemperature({ scores, hardDeadline }: QualifyInput): Temp | null {
  if (hardDeadline) return "hot"; // hard override (docs/09 §2 step 3)
  const timeline = scores.timeline;
  if (!timeline) return null; // Timeline is the base — required
  let t: Temp = timeline;
  if (scores.reason === "hot") t = escalate(t); // motivation escalation
  return t;
}
